import React, { useState } from "react";
import styled from "styled-components";
import { colors } from "../../../../styles/style";
import { EditModal } from "../../../shared/EditModal";
import {
  CustomDateInput,
  CustomTextInput,
} from "../../../../helpers/components/CustomInputs";
import Button from "../../../../helpers/components/Button";
import { SharedButton } from "../../../settings/common/shared/SharedButton";
import penIcon from "../../../../images/penIconBlack.svg";
import { addFirstWord, updateFirstWord } from "../../../../api";
import { useBabyContext } from "../../../../context/BabyContext";
import { FirstWord } from "../../../../interface/FirstWordInterface";
import { firstWordLimits } from "../../../../common/constants";
import {
  dateInputValueToLocalNoonTimestamp,
  formatDateForInputValue,
  getApiErrorStatus,
} from "../../../../common/helpers";
import {
  FirstWordSheetHeader,
  HTTP_STATUS_CONFLICT,
  HTTP_STATUS_NOT_FOUND,
  SheetErrorText,
  firstWordErrorMessages,
  formatAgeOldAt,
} from "./FirstWordsShared";
import { useCanEdit } from "../../../../common/useCanEdit";

interface FirstWordModalProps {
  word?: FirstWord;
  dateOfBirth?: number;
  onClose: () => void;
  onSaved: () => void;
  onWordGone: () => void;
}

export const FirstWordModal: React.FC<FirstWordModalProps> = ({
  word,
  dateOfBirth,
  onClose,
  onSaved,
  onWordGone,
}) => {
  const canEdit = useCanEdit("first_words");
  const { selectedBabyId } = useBabyContext();
  const today = formatDateForInputValue(new Date());
  const minDate =
    dateOfBirth === undefined
      ? undefined
      : formatDateForInputValue(new Date(dateOfBirth));

  const [text, setText] = useState(word?.word ?? "");
  const [soundedLike, setSoundedLike] = useState(word?.sounded_like ?? "");
  const [heardBy, setHeardBy] = useState(word?.heard_by ?? "");
  const [saidOn, setSaidOn] = useState(
    word ? formatDateForInputValue(new Date(word.said_on * 1000)) : today
  );
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const babyId = word?.baby_id ?? selectedBabyId;
  const isDateValid =
    saidOn !== "" &&
    saidOn <= today &&
    (minDate === undefined || saidOn >= minDate);
  const canSave =
    canEdit && !saving && !!babyId && text.trim() !== "" && isDateValid;
  const ageHint =
    dateOfBirth !== undefined && isDateValid
      ? formatAgeOldAt(dateOfBirth, dateInputValueToLocalNoonTimestamp(saidOn))
      : undefined;

  const handleSubmit = async () => {
    if (!canSave || !babyId) return;
    setSaving(true);
    setErrorMessage("");

    const firstWord = {
      baby_id: babyId,
      word: text.trim(),
      said_on: dateInputValueToLocalNoonTimestamp(saidOn),
    };
    try {
      if (word) {
        // Edits always send the optional fields so clearing one removes it.
        await updateFirstWord(babyId, word.id, {
          ...firstWord,
          sounded_like: soundedLike.trim(),
          heard_by: heardBy.trim(),
        });
      } else {
        await addFirstWord({
          ...firstWord,
          sounded_like: soundedLike.trim() || undefined,
          heard_by: heardBy.trim() || undefined,
        });
      }
      onSaved();
    } catch (error) {
      const status = getApiErrorStatus(error);
      if (word && status === HTTP_STATUS_NOT_FOUND) {
        onWordGone();
        return;
      }
      console.error("Error saving first word", error);
      setErrorMessage(
        status === HTTP_STATUS_CONFLICT
          ? firstWordErrorMessages.duplicate
          : firstWordErrorMessages.generic
      );
      setSaving(false);
    }
  };

  return (
    <EditModal isOpen={true} onClose={onClose}>
      <FirstWordSheetHeader
        title={word ? "Edit Word" : "Add Word"}
        onClose={onClose}
      />
      <FirstWordForm>
        <CustomTextInput
          label="Word"
          placeholder="e.g. Mama"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={firstWordLimits.word}
        />
        <CharacterCounter>
          {text.length}/{firstWordLimits.word}
        </CharacterCounter>
        <CustomTextInput
          label="How did it sound? (optional)"
          placeholder="e.g. mum-mum"
          value={soundedLike}
          onChange={(e) => setSoundedLike(e.target.value)}
          maxLength={firstWordLimits.soundedLike}
        />
        <CharacterCounter>
          {soundedLike.length}/{firstWordLimits.soundedLike}
        </CharacterCounter>
        <CustomDateInput
          label="When did they say it?"
          placeholder="Date"
          value={saidOn}
          onChange={(e) => setSaidOn(e.target.value)}
          min={minDate}
          max={today}
          hint={ageHint}
        />
        <CustomTextInput
          label="Who heard it? (optional)"
          placeholder="e.g. Grandma, at breakfast"
          value={heardBy}
          onChange={(e) => setHeardBy(e.target.value)}
          maxLength={firstWordLimits.heardBy}
        />
        <CharacterCounter>
          {heardBy.length}/{firstWordLimits.heardBy}
        </CharacterCounter>
        {errorMessage && (
          <SheetErrorText role="alert">{errorMessage}</SheetErrorText>
        )}
        <SharedButton
          iconSrc={penIcon}
          buttonText="Done"
          onClick={handleSubmit}
          loading={saving}
          $disabled={!canSave}
        />
        <Button $button_type="colored" onClick={onClose}>
          Cancel
        </Button>
      </FirstWordForm>
    </EditModal>
  );
};

const FirstWordForm = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  padding: 0 12px;
`;

const CharacterCounter = styled.div`
  align-self: flex-end;
  margin: -9px 0 8px;
  color: ${colors.softBrown};
  font-size: 14px;
`;
